// apps/client/src/components/reports/avsV2/FindPaymentPanel.tsx
// "Find the payment" inside the Review panel: search the company's own lender
// statement loans by client name or loan number and link one to this deal.
//
// Opens on the loans settled near the deal at a similar amount. Linking is the
// same decision as "Confirm payment" (decideDealLoanLink): the deal shows Paid
// straight away, the person is named in Activity, and "Not this payment"
// undoes it. A loan another deal already holds is shown but cannot be linked.

import React, { useCallback, useEffect, useId, useState } from 'react';
import {
  AWAITING_PAYMENT_SEARCH_MAX,
  type AwaitingPaymentLoanSearchResult,
} from '@valutten/shared-schemas/dist/approvedVsSettledV2';
import { formatCurrencyShorthand } from '../../../utils/formatters';
import { decideAwaitingPayment, searchAwaitingPaymentLoans } from '../../../hooks/reports/useAwaitingPayment';
import { EMPTY } from './avsV2Format';

export const FIND_NEARBY_COPY = 'Loans settled near this deal at a similar amount. Search by client name or loan number to look further.';
export const FIND_EMPTY_COPY = 'No statement loans match. Try the surname only, or the loan number.';
export const FIND_TRUNCATED_COPY = 'More loans match than are shown. Add more of the name, or use the loan number.';

interface FindPaymentPanelProps {
  companyId: string;
  dealId: string;
  /** A loan was linked: refresh the list and close the panel. */
  onLinked: () => void;
  onCancel: () => void;
}

const fieldCls =
  'bg-gray-700 border border-gray-600 rounded px-2.5 py-[0.4375rem] text-[0.8125rem] text-gray-200 w-full focus:outline-none focus:border-blue-500';

export const FindPaymentPanel: React.FC<FindPaymentPanelProps> = ({ companyId, dealId, onLinked, onCancel }) => {
  const inputId = useId();
  const [text, setText] = useState('');
  const [searched, setSearched] = useState<string | null>(null);
  const [results, setResults] = useState<AwaitingPaymentLoanSearchResult[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [choosing, setChoosing] = useState<AwaitingPaymentLoanSearchResult | null>(null);
  const [linking, setLinking] = useState(false);

  const search = useCallback(async (query: string) => {
    setLoading(true);
    setError(null);
    setChoosing(null);
    try {
      const res = await searchAwaitingPaymentLoans({ companyId, dealId, query: query || undefined });
      setResults(res.results);
      setTruncated(res.truncated);
      setSearched(query);
    } catch (err) {
      setError((err as { message?: string } | null)?.message || 'Search failed. Try again.');
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [companyId, dealId]);

  // Open on the loans near this deal.
  useEffect(() => {
    void search('');
  }, [search]);

  const link = async (r: AwaitingPaymentLoanSearchResult) => {
    setLinking(true);
    setError(null);
    try {
      await decideAwaitingPayment({ companyId, dealId, action: 'confirm', loanIds: [r.loanId] });
      onLinked();
    } catch (err) {
      setError((err as { message?: string } | null)?.message || 'Could not link this payment. Try again.');
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="mt-3 bg-gray-800 rounded-lg p-3.5 flex flex-col gap-3" data-form="find">
      <form
        className="flex gap-2"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          void search(text.trim());
        }}
      >
        <label htmlFor={inputId} className="sr-only">Client name or loan number</label>
        <input
          id={inputId}
          type="search"
          value={text}
          maxLength={AWAITING_PAYMENT_SEARCH_MAX}
          onChange={(e) => setText(e.target.value)}
          placeholder="Client name or loan number"
          className={fieldCls}
          autoComplete="off"
        />
        <button
          type="submit"
          disabled={loading}
          className="px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-colors disabled:opacity-50 flex-shrink-0"
        >
          Search
        </button>
      </form>

      {searched === '' && results && results.length > 0 && <p className="text-xs text-gray-400 leading-normal">{FIND_NEARBY_COPY}</p>}
      {loading && <p className="text-xs text-gray-400" aria-live="polite">Searching…</p>}
      {error && <p className="text-xs text-red-400" role="alert">{error}</p>}
      {!loading && results && results.length === 0 && <p className="text-xs text-gray-400">{FIND_EMPTY_COPY}</p>}

      {!loading && results && results.length > 0 && (
        <ul className="flex flex-col gap-2" aria-label="Statement loans">
          {results.map((r) => (
            <li key={r.loanId} className="border border-gray-700 rounded-lg px-3 py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white break-words">{r.clientName || 'Unnamed client'}</div>
                  <div className="mt-0.5 text-xs text-gray-400 break-words">
                    {[r.lender || EMPTY, `Loan ${r.loanId}`, r.settledDate ? `settled ${r.settledDate}` : null].filter(Boolean).join(' · ')}
                  </div>
                  <div className="mt-1 text-xs text-gray-300 tabular-nums">
                    Paid on {formatCurrencyShorthand(r.paidOnCents)} · Upfront {formatCurrencyShorthand(r.upfrontCents)}
                  </div>
                </div>
                {r.linkedElsewhere ? (
                  <span className="text-[0.6875rem] text-gray-500 text-right flex-shrink-0 max-w-[7rem]">Linked to another deal</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setChoosing(r)}
                    disabled={linking}
                    className="px-2.5 py-1.5 text-[0.8125rem] font-medium rounded-lg border bg-emerald-500/[0.12] border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/20 hover:text-white flex-shrink-0 disabled:opacity-50"
                  >
                    Link
                  </button>
                )}
              </div>
              {choosing?.loanId === r.loanId && (
                <div className="mt-2.5 pt-2.5 border-t border-gray-700 flex flex-col gap-2">
                  <p className="text-[0.8125rem] text-gray-300 leading-normal">
                    Link loan {r.loanId} ({r.clientName || 'unnamed client'}) to this deal? The deal is marked Paid straight
                    away, you are named in Activity, and Not this payment undoes it.
                  </p>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setChoosing(null)} className="px-3 py-1.5 text-sm font-medium text-gray-300 hover:text-white">
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={linking}
                      onClick={() => void link(r)}
                      className="px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-colors disabled:opacity-50"
                    >
                      Link payment
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {!loading && truncated && <p className="text-xs text-gray-400">{FIND_TRUNCATED_COPY}</p>}

      <div className="flex justify-end">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm font-medium text-gray-300 hover:text-white">
          Close search
        </button>
      </div>
    </div>
  );
};

export default FindPaymentPanel;
